import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, apiErrorMessage } from '@/lib/api'
import type { SessionResponse } from '@/lib/types'
import { useAuth } from '@/stores/authStore'

export default function RegisterPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [workspaceName, setWorkspaceName] = useState('')
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { setSession } = useAuth()

  const mut = useMutation({
    mutationFn: async (vars: typeof state) => {
      const r = await apiClient.post<SessionResponse & { token: string }>('/api/v1/auth/register', vars)
      return r.data
    },
    onSuccess: (data) => {
      setSession(data.user, data.workspace)
      qc.setQueryData(['session'], { user: data.user, workspace: data.workspace })
      navigate('/dashboard', { replace: true })
    },
  })

  const state = { name, email, password, workspaceName }

  return (
    <div className="w-full max-w-sm rounded border bg-white p-6 shadow-sm">
      <h1 className="text-xl font-semibold mb-4">Create your workspace</h1>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          mut.mutate(state)
        }}
      >
        <label className="block text-sm">
          Your name
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          Workspace name
          <input
            required
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          Email
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          Password (min 8 chars)
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
        {mut.error && (
          <p className="text-sm text-red-600">{apiErrorMessage(mut.error)}</p>
        )}
        <button
          type="submit"
          disabled={mut.isPending}
          className="w-full rounded bg-neutral-900 text-white text-sm py-2 disabled:opacity-50"
        >
          {mut.isPending ? 'Creating…' : 'Create workspace'}
        </button>
      </form>
      <p className="mt-4 text-sm text-neutral-600">
        Have an account?{' '}
        <Link to="/login" className="text-neutral-900 underline">
          Sign in
        </Link>
      </p>
    </div>
  )
}
